import { inject, Injectable } from '@angular/core'
import { CaseType, HearingType } from '@ftr/annotations-contracts'
import { Uuid } from '@ftr/contracts/type/shared'
import { RemoteData, tapFailure, unwrapData } from '@ftr/foundation'
import { Action, createSelector, Selector, State, StateContext, StateOperator } from '@ngxs/store'
import { patch, ɵPatchSpec } from '@ngxs/store/operators'
import { memoize } from 'lodash-es'
import { firstValueFrom, Observable } from 'rxjs'
import { AnnotationTypesService } from '../../services'
import { FetchAnnotationTypesCommand, InitialiseAnnotationTypesCommand } from './annotation-types.commands'
import { AnnotationTypesInstanceStateModel, AnnotationTypesStateModel } from './annotation-types.model'

export const getDefaultAnnotationTypesState: () => AnnotationTypesStateModel = () => ({
  instances: {},
})

@State({
  name: 'annotationTypes',
  defaults: getDefaultAnnotationTypesState(),
})
@Injectable()
export class AnnotationTypesState {
  private readonly annotationTypeService = inject(AnnotationTypesService)

  @Selector()
  static allInstanceStates(state: AnnotationTypesStateModel): Record<Uuid, AnnotationTypesInstanceStateModel> {
    return state?.instances ?? {}
  }

  static readonly annotationTypes = memoize(
    (
      courtSystemId: string,
    ): ((
      sourceStates: ReturnType<typeof AnnotationTypesState.allInstanceStates>,
    ) => AnnotationTypesInstanceStateModel) =>
      createSelector([AnnotationTypesState.allInstanceStates], sourceStates => {
        return sourceStates[courtSystemId] ?? {}
      }),
    courtSystemId => courtSystemId,
  )

  static readonly hearingTypes = memoize(
    (
      courtSystemId: string,
    ): ((sourceStates: ReturnType<typeof AnnotationTypesState.allInstanceStates>) => HearingType[]) =>
      createSelector([AnnotationTypesState.allInstanceStates], sourceStates => {
        const { hearingTypes = [] } = sourceStates[courtSystemId] ?? {}

        return hearingTypes
      }),
    courtSystemId => courtSystemId,
  )

  static readonly caseTypes = memoize(
    (
      courtSystemId: string,
    ): ((sourceStates: ReturnType<typeof AnnotationTypesState.allInstanceStates>) => CaseType[]) =>
      createSelector([AnnotationTypesState.allInstanceStates], sourceStates => {
        const { caseTypes = [] } = sourceStates[courtSystemId] ?? {}

        return caseTypes
      }),
    courtSystemId => courtSystemId,
  )

  @Action(InitialiseAnnotationTypesCommand)
  private initialiseAnnotationTypesCommand(
    { setState, getState, dispatch }: StateContext<AnnotationTypesStateModel>,
    { courtSystemId, audioSegmentId }: InitialiseAnnotationTypesCommand,
  ): Observable<void> | undefined {
    const { status } = getState().instances[courtSystemId] ?? {}
    if (status?.isCompleted() || status?.isLoading()) {
      return
    }

    setState(
      makeInstancePatch(courtSystemId, {
        hearingTypes: [],
        caseTypes: [],
        status: RemoteData.notAsked(),
      }),
    )

    return dispatch(new FetchAnnotationTypesCommand(courtSystemId, audioSegmentId))
  }

  @Action(FetchAnnotationTypesCommand)
  private async fetchAnnotationTypesCommand(
    { getState, setState }: StateContext<AnnotationTypesStateModel>,
    { courtSystemId, audioSegmentId }: FetchAnnotationTypesCommand,
  ): Promise<void> {
    const { status } = getState().instances[courtSystemId] ?? {}

    if (status.isLoading()) {
      return
    }

    setState(
      makeInstancePatch(courtSystemId, {
        status: RemoteData.loading(),
      }),
    )

    try {
      const { hearingTypes, caseTypes } = await firstValueFrom(
        this.annotationTypeService.getAnnotationTypes(courtSystemId, audioSegmentId).pipe(
          tapFailure(err => {
            throw err
          }),
          unwrapData(),
        ),
      )

      setState(
        makeInstancePatch(courtSystemId, {
          hearingTypes,
          caseTypes,
          status: RemoteData.success(null),
        }),
      )
    } catch (err) {
      setState(
        makeInstancePatch(courtSystemId, {
          hearingTypes: [],
          caseTypes: [],
          status: RemoteData.failure(new Error('Failed to fetch annotation types', { cause: err })),
        }),
      )
    }
  }
}

function makeInstancePatch(
  instanceId: string,
  instancePartial: ɵPatchSpec<AnnotationTypesInstanceStateModel>,
): StateOperator<AnnotationTypesStateModel> {
  return patch({
    instances: patch({
      [instanceId]: patch(instancePartial),
    }),
  })
}
